import { cn } from '@ophelos/ui/lib/utils';
import { Circle, LoaderCircle } from 'lucide-react';

export interface LoaderProps {
  /**
   * Optional text to display below the loader
   */
  text?: string;
  /**
   * Size variant for the loader
   * @default "default"
   */
  size?: 'xs' | 'sm' | 'default' | 'lg';
  /**
   * Additional classes for the container
   */
  className?: string;
  /**
   * Whether to center the loader in the viewport
   * @default false
   */
  fullScreen?: boolean;

  colour?: 'default' | 'muted' | 'primary';
}

const sizeMap = {
  xs: {
    circle: 'size-4',
    spinner: 'size-4.5 -top-0.25 -left-0.25',
    text: 'text-xs',
    margin: 'mt-1',
  },
  sm: {
    circle: 'size-6',
    spinner: 'size-7 -top-0.5 -left-0.5',
    text: 'text-sm',
    margin: 'mt-2',
  },
  default: {
    circle: 'size-9',
    spinner: 'size-10 -top-0.5 -left-0.5',
    text: 'text-base',
    margin: 'mt-4',
  },
  lg: {
    circle: 'size-12',
    spinner: 'size-13 -top-0.5 -left-0.5',
    text: 'text-base',
    margin: 'mt-4',
  },
};

const colourMap = {
  default: 'text-ring',
  primary: 'text-primary-foreground',
  muted: 'text-muted-foreground/75',
};

/**
 * Loader component with animated spinner and optional text
 *
 * @example
 * ```tsx
 * // Default loader
 * <Loader text="Loading..." />
 *
 * // Small loader without text
 * <Loader size="sm" />
 *
 * // Full screen loader
 * <Loader text="Completing sign in..." fullScreen />
 *
 * // Large loader with custom styling
 * <Loader size="lg" text="Processing..." className="bg-background/80" />
 * ```
 */
export function Loader({ text, size = 'default', className, fullScreen = false, colour: colourProp = 'default' }: LoaderProps) {
  const sizes = sizeMap[size];
  const colourClass = colourMap[colourProp];

  return (
    <div className={cn('stack items-center justify-center', fullScreen && 'min-h-screen', className)}>
      <div className="relative">
        <Circle className={cn(sizes.circle, 'text-muted')} />
        <LoaderCircle className={cn('absolute animate-spin', colourClass, sizes.spinner)} />
      </div>
      {text && <p className={cn('text-muted-foreground text-center', sizes.text, sizes.margin)}>{text}</p>}
    </div>
  );
}
